// Upload PDF (spec C8): after the person picks a file, this asks what it is (Renewal dec page, ID
// cards, Carrier invoice, Other) and sends it. Markup from design/kit/components/Dialog, with the
// option chips from design/kit/components/Chip (`fopts`).
import { useState } from 'react';
import type { Api, UploadKind, Uploaded } from '../auth/api';
import { Dialog } from '../shell/Dialog';
import { fileSize, uploadError, uploadKinds } from './upload-lines';

type Props = {
  api: Api;
  renewalId: string;
  file: File;
  /** What the file is taken to be when the dialog opens (Replace starts on Renewal dec page). */
  kind: UploadKind;
  onClose: () => void;
  onUploaded: (result: Uploaded, kind: UploadKind) => void;
};

export function UploadDialog({ api, renewalId, file, kind: first, onClose, onUploaded }: Props) {
  const [kind, setKind] = useState<UploadKind>(first);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setBusy(true);
    setError(null);
    const res = await api.uploadDocument(renewalId, file, file.name, kind);
    setBusy(false);
    if (res.ok) return onUploaded(res.body, kind);
    setError(uploadError(res.status, res.error));
  }

  return (
    <Dialog
      id="upload-title"
      title="Upload PDF"
      subline={`${file.name}, ${fileSize(file.size)}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary ml-auto"
            disabled={busy}
            onClick={() => void send()}
          >
            {busy && <span className="spin" />}
            {busy ? 'Uploading' : 'Upload'}
          </button>
        </>
      }
    >
      <div role="group" aria-labelledby="upload-kind">
        <div id="upload-kind" className="flab">
          What is it?
        </div>
        <div className="fopts">
          {uploadKinds.map((k) => (
            <button
              key={k.kind}
              type="button"
              className={k.kind === kind ? 'chip on' : 'chip'}
              aria-pressed={k.kind === kind}
              onClick={() => setKind(k.kind)}
            >
              {k.label}
            </button>
          ))}
        </div>
      </div>
      {error && (
        <p className="ferr" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}
